import { IonText } from "@ionic/react";
import { createRef, DetailedHTMLProps, TextareaHTMLAttributes } from "react";
import { Control, FieldError, FieldValues, Path, useController } from "react-hook-form";
import { useIntl } from "react-intl";

import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import styles from "@components/fields/Textarea.module.css";
import Icon from "@components/text/Icon";
import useAutofocus from "@hooks/useAutofocus";
import { generateHTMLId } from "@utils/htmlUtils";

type Props<FormData extends FieldValues> = Omit<
  DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>,
  // don't let clients use props we are generating in the component
  "aria-describedby" | "aria-invalid" | "name" | "onBlur" | "onChange" | "ref" | "value"
> & {
  control: Control<FormData>;
  error: FieldError | undefined;
  label: string;
  name: Path<FormData>; // stronger type than what textarea does natively to work with react-hook-form
};

const Textarea = <FormData extends FieldValues>({
  autoFocus,
  control,
  error,
  id,
  label,
  name,
  ...textareaProps
}: Props<FormData>) => {
  const ref = createRef<HTMLTextAreaElement>();
  const { field } = useController({ control, name });
  const intl = useIntl();

  useAutofocus(!!autoFocus, ref);

  const hasError = !!error && !!error.message;
  const wrapperClasses = [styles.textareaContainer];
  if (hasError) wrapperClasses.push(styles.hasError);
  const textareaId = id ?? generateHTMLId();

  return (
    <>
      <div
        className={wrapperClasses.join(" ")}
        onClick={() => {
          ref.current?.focus();
        }}
      >
        <div className={styles.labelContainer}>
          <label htmlFor={textareaId}>
            {label}
            {textareaProps.required && <RequiredFieldIndicator />}
            {hasError && (
              <div className="ion-float-end">
                <Icon color="danger" icon="circleExclamation" />
              </div>
            )}
          </label>
        </div>
        <textarea
          // default values that can be overwritten by textareaProps
          autoComplete="off"
          autoCorrect="on"
          placeholder={intl.formatMessage({ id: "forms.defaults.placeholder.textarea" })}
          {...textareaProps}
          aria-describedby={hasError ? `${textareaId}-error` : undefined}
          aria-invalid={hasError}
          id={textareaId}
          name={name}
          onBlur={field.onBlur}
          onChange={field.onChange}
          ref={ref}
          value={field.value ?? ""}
        />
      </div>
      {hasError && error.message && (
        <div className={styles.error} id={`${textareaId}-error`}>
          <IonText color="danger">{error.message}</IonText>
        </div>
      )}
    </>
  );
};
export default Textarea;
